import { notification } from "antd";
import { apiService } from "../services/apiServices";
import { cleanObject } from "./cleanObject";
import _ from "lodash";

const MAX_PARALLEL_CALLS = 5;
const MAX_BULK_ORDER = 500;

const createOrders = async (authParams, orderIds, orderData) => {
  const createOrdersPayload = orderIds.map((orderId) =>
    combineOrdersToPayload(orderData[orderId])
  );

  const chunkedOrderArray = _.chunk(createOrdersPayload, MAX_BULK_ORDER);

  for (const orderChunk of chunkedOrderArray) {
    await apiService.createBulkOrders(orderChunk, authParams);
  }

  if (orderIds?.length) {
    notification.success({
      message: "Orders Created Successfully",
      placement: "bottomRight",
    });
  } else {
    notification.warning({
      message: "No Orders Found To Create",
      placement: "bottomRight",
    });
  }

  const validateOrders = await apiService.validateOrderIds(
    {
      entityIds: orderIds,
      allowInactive: false,
    },
    authParams
  );

  const { entityIds: createdOrderIds = [], availableIds: failedOrderIds = [] } =
    validateOrders?.data || {};

  return { orderIds: createdOrderIds, failedIds: failedOrderIds };
};

const updateOrders = async (authParams, orderIds, orderData) => {
  const requireOrderStatus = [
    "RECEIVED",
    "OPEN",
    "PARKED",
    "PLANNING",
    "PLANNED",
  ];
  const chunkedOrders = _.chunk(orderIds, MAX_PARALLEL_CALLS);
  const promises = [];
  const updatedOrders = [];
  const failedOrders = [];
  for (const orderChunk of chunkedOrders) {
    for (const orderId of orderChunk) {
      const promise = (async () => {
        const omsOrderData = await apiService.getOneOrder(orderId, authParams);
        if (requireOrderStatus.includes(omsOrderData?.data?.orderStatus)) {
          const {
            isUpdateRequired,
            omsCustomProperties,
            omsLineItems,
            newVolume,
            newQuantity,
          } = compareAndMergeOrderData(omsOrderData, orderData[orderId]);
          if (isUpdateRequired) {
            const { status = false } = await apiService.updateOrders(
              orderId,
              {
                customProperties: omsCustomProperties,
                lineItems: omsLineItems,
                volume: newVolume,
                quantity: newQuantity,
              },
              authParams
            );
            if (status) {
              updatedOrders.push(orderId);
            } else {
              failedOrders.push(orderId);
            }
          }
        }
      })();
      promises.push(promise);
    }

    // Limit the number of parallel calls
    if (promises.length >= MAX_PARALLEL_CALLS) {
      await Promise.all(promises);
      promises.length = 0;
    }
  }
  // Ensure any remaining promises are resolved
  await Promise.all(promises);

  if (updatedOrders?.length) {
    notification.success({
      message: "Orders Updated Successfully",
      placement: "bottomRight",
    });
  } else {
    notification.warning({
      message: "No Orders Found To Update",
      placement: "bottomRight",
    });
  }

  return { orderIds: updatedOrders, failedIds: failedOrders };
};

function combineOrdersToPayload(orderData) {
  if (orderData.length === 1) {
    return {
      ...orderData[0],
      volume: {
        value: orderData[0].lineItems[0].parts[0].volume.value,
        unit: orderData[0].lineItems[0].parts[0].volume.unit,
      },
      quantity: {
        value: orderData[0].lineItems[0].quantity,
        unit: orderData[0].lineItems[0].quantityUnit,
      },
    };
  }

  const finalOrder = _.cloneDeep(orderData[0]);
  finalOrder.customProperties = {
    FULLCASE: 0,
    KBOX: 0,
    TOTE: 0,
    POP: 0,
    "TOTAL-CASE": 0,
  };
  finalOrder.lineItems = [];
  const skillsSet = new Set();
  const categoriesSet = new Set();
  //take the first available value from the rows of same order
  for (const order of orderData) {
    finalOrder.teamId = finalOrder?.teamId || order?.teamId;
    finalOrder.type = finalOrder?.type || order?.type;
    finalOrder.orderDate = finalOrder?.orderDate || order?.orderDate;
    finalOrder.homebaseId = finalOrder?.homebaseId || order?.homebaseId;
    finalOrder.locationId = finalOrder?.locationId || order?.locationId;

    finalOrder.customProperties = {
      FULLCASE:
        Number(finalOrder?.customProperties?.FULLCASE) +
        Number(order?.customProperties?.FULLCASE),
      KBOX:
        Number(finalOrder?.customProperties?.KBOX) +
        Number(order?.customProperties?.KBOX),
      TOTE:
        Number(finalOrder?.customProperties?.TOTE) +
        Number(order?.customProperties?.TOTE),
      POP:
        Number(finalOrder?.customProperties?.POP) +
        Number(order?.customProperties?.POP),
      "TOTAL-CASE":
        Number(finalOrder?.customProperties?.["TOTAL-CASE"]) +
        Number(order?.customProperties?.["TOTAL-CASE"]),
    };

    if (order?.lineItems?.length) {
      for (const lineItem of order.lineItems) {
        const existingLineItemArray = finalOrder.lineItems.filter(
          (e) => e.id === lineItem.id && e.lineItemId === lineItem.lineItemId
        );
        if (existingLineItemArray && existingLineItemArray.length > 0) {
          // Update existing line item
          const existingLineItem = existingLineItemArray[0];

          if (
            lineItem?.quantity !== existingLineItem?.quantity &&
            !_.isNaN(parseFloat(lineItem?.quantity))
          ) {
            existingLineItem.quantity =
              parseFloat(lineItem?.quantity).toString() ??
              existingLineItem?.quantity;
          }

          if (lineItem?.quantityUnit !== existingLineItem?.quantityUnit) {
            existingLineItem.quantityUnit =
              lineItem?.quantityUnit ?? existingLineItem?.quantityUnit;
          }

          existingLineItem.parts = existingLineItem.parts.map((part) => {
            if ("volume" in part) {
              const lineItemVolumePart = lineItem.parts.find(
                (lineItemPart) => "volume" in lineItemPart
              );
              if (lineItemVolumePart) {
                if (
                  part?.volume?.value !== lineItemVolumePart?.volume?.value &&
                  !_.isNaN(parseFloat(lineItemVolumePart?.volume?.value))
                ) {
                  part.volume.value =
                    parseFloat(lineItemVolumePart?.volume?.value).toString() ??
                    part?.volume?.value;
                }
                if (part?.volume?.unit !== lineItemVolumePart?.volume?.unit) {
                  part.volume.unit =
                    lineItemVolumePart?.volume?.unit ?? part?.volume?.unit;
                }
              }
            }
            return part;
          });
        } else {
          finalOrder.lineItems.push(lineItem);
        }
      }
    }
  }
  finalOrder.volume = {
    value: finalOrder.lineItems.reduce((acc, lineItem) => {
      return acc + lineItem.parts[0].volume.value;
    }, 0),
    unit: finalOrder.lineItems[0].parts[0].volume.unit,
  };
  finalOrder.quantity = {
    value: finalOrder.lineItems.reduce((acc, lineItem) => {
      return acc + lineItem.quantity;
    }, 0),
    unit: finalOrder.lineItems[0].quantityUnit,
  };
  finalOrder.skills = Array.from(skillsSet);
  finalOrder.category = Array.from(categoriesSet).join(",");
  return cleanObject(finalOrder);
}

function compareAndMergeOrderData(omsOrderData, csvOrderData) {
  let omsCustomProperties = _.cloneDeep(
    _.get(omsOrderData, "data.customProperties", {})
  );
  let newCustomProperties = {
    FULLCASE: 0,
    KBOX: 0,
    TOTE: 0,
    POP: 0,
    "TOTAL-CASE": 0,
  };
  const omsLineItems = _.cloneDeep(_.get(omsOrderData, "data.lineItems", []));

  for (const order of csvOrderData) {
    let orderCustomProperties = order?.customProperties || {};
    newCustomProperties = {
      FULLCASE:
        Number(newCustomProperties?.FULLCASE) +
        Number(orderCustomProperties?.FULLCASE),
      KBOX:
        Number(newCustomProperties?.KBOX) + Number(orderCustomProperties?.KBOX),
      TOTE:
        Number(newCustomProperties?.TOTE) + Number(orderCustomProperties?.TOTE),
      POP:
        Number(newCustomProperties?.POP) + Number(orderCustomProperties?.POP),
      "TOTAL-CASE":
        Number(newCustomProperties?.["TOTAL-CASE"]) +
        Number(orderCustomProperties?.["TOTAL-CASE"]),
    };
    const orderLineItems = order?.lineItems || [];

    //line items update
    for (const lineItem of orderLineItems) {
      const existingLineItemArray = omsLineItems.filter(
        (e) => e.id === lineItem.id && e.lineItemId === lineItem.lineItemId
      );

      if (existingLineItemArray?.length) {
        let existingLineItem = existingLineItemArray[0];

        if (
          lineItem?.quantity !== existingLineItem?.quantity &&
          !_.isNaN(parseFloat(lineItem?.quantity))
        ) {
          existingLineItem.quantity =
            parseFloat(lineItem?.quantity).toString() ??
            existingLineItem?.quantity;
        }

        if (lineItem?.quantityUnit !== existingLineItem?.quantityUnit) {
          existingLineItem.quantityUnit =
            lineItem?.quantityUnit ?? existingLineItem?.quantityUnit;
        }

        existingLineItem.parts = existingLineItem.parts.map((part) => {
          if ("volume" in part) {
            const lineItemVolumePart = lineItem.parts.find(
              (lineItemPart) => "volume" in lineItemPart
            );
            if (lineItemVolumePart) {
              if (
                part?.volume?.value !== lineItemVolumePart?.volume?.value &&
                !_.isNaN(parseFloat(lineItemVolumePart?.volume?.value))
              ) {
                part.volume.value =
                  parseFloat(lineItemVolumePart?.volume?.value).toString() ??
                  part?.volume?.value;
              }
              if (part?.volume?.unit !== lineItemVolumePart?.volume?.unit) {
                part.volume.unit =
                  lineItemVolumePart?.volume?.unit ?? part?.volume?.unit;
              }
            }
          }
          return part;
        });
      } else {
        omsLineItems.push(lineItem);
      }
    }
  }

  if (
    Number(omsCustomProperties["KBOX"]) !==
      Number(newCustomProperties["KBOX"]) ||
    Number(omsCustomProperties["TOTE"]) !==
      Number(newCustomProperties["TOTE"]) ||
    Number(omsCustomProperties["FULLCASE"]) !==
      Number(newCustomProperties["FULLCASE"]) ||
    Number(omsCustomProperties["TOTAL-CASE"]) !==
      Number(newCustomProperties["TOTAL-CASE"])
  ) {
    omsCustomProperties = _.merge(omsCustomProperties, newCustomProperties);
  }
  const newVolume = {
    value: omsLineItems.reduce((acc, lineItem) => {
      return Number(acc) + Number(lineItem.parts[0].volume.value);
    }, 0),
    unit: omsLineItems[0].parts[0].volume.unit,
  };
  const newQuantity = {
    value: omsLineItems.reduce((acc, lineItem) => {
      return Number(acc) + Number(lineItem.quantity);
    }, 0),
    unit: omsLineItems[0].quantityUnit,
  };

  const isUpdateRequired =
    JSON.stringify({
      customProperties: omsCustomProperties,
      lineItems: omsLineItems,
      volume: newVolume,
      quantity: newQuantity,
    }) !==
    JSON.stringify({
      customProperties: omsOrderData?.data?.customProperties,
      lineItems: omsOrderData?.data?.lineItems,
      volume: {
        ...omsOrderData?.data?.volume,
        value: Number(omsOrderData?.data?.volume?.value),
      },
      quantity: {
        ...omsOrderData?.data?.quantity,
        value: Number(omsOrderData?.data?.quantity?.value),
      },
    });
  return {
    isUpdateRequired,
    omsCustomProperties,
    omsLineItems,
    newVolume,
    newQuantity,
  };
}

export { createOrders, updateOrders };
